import { publicRoutes } from '@/shared/lib/public-routes';
import { HTTP_STATUS } from '../consts';
import { ErrorCodeEntry } from './types';

export const ERROR_CODE_REGISTRY: Record<string, ErrorCodeEntry> = {
  // ── Common ──
  // COM_000~999: HTTP status code 기반으로 getStatusEntry()에서 처리

  // ── Product ──
  /** General error */
  PRODUCT_000: { action: { type: 'toast' } },
  /** 상세페이지 생성 실패 */
  PRODUCT_001: { action: { type: 'toast' }, i18nKey: 'PRODUCT_001' },
  /** 상품 반영 실패 */
  PRODUCT_002: { action: { type: 'toast' }, i18nKey: 'PRODUCT_002' },
  /** 썸네일 이미지 교체 실패 */
  PRODUCT_003: { action: { type: 'toast' }, i18nKey: 'PRODUCT_003' },
  /** 이미지 유형 변경 실패 */
  PRODUCT_004: { action: { type: 'toast' }, i18nKey: 'PRODUCT_004' },
  /** 이미지 삭제 실패 */
  PRODUCT_005: { action: { type: 'toast' }, i18nKey: 'PRODUCT_005' },
  /** 상품 조회 결과 없음 */
  PRODUCT_006: { action: { type: 'stay' } },
  /** 에셋 추가 실패 */
  PRODUCT_007: { action: { type: 'stay' } },
  /** presigned url 생성 실패 */
  PRODUCT_008: { action: { type: 'toast' }, i18nKey: 'PRODUCT_008' },
  /** 애셋 not found */
  PRODUCT_009: { action: { type: 'toast' }, i18nKey: 'PRODUCT_009' },
  /** 상품 삭제 실패 */
  PRODUCT_010: { action: { type: 'toast' }, i18nKey: 'PRODUCT_010' },
  /** 상품 조회 실패 */
  PRODUCT_011: { action: { type: 'toast' }, i18nKey: 'PRODUCT_011' },

  // ── Detail (시안) ──
  /** 시안 조회 실패 */
  DETAIL_001: { action: { type: 'toast' }, i18nKey: 'DETAIL_001' },
  /** 시안 생성 불가 */
  DETAIL_002: { action: { type: 'toast' }, i18nKey: 'DETAIL_002' },
  /** 시안 이름 변경 실패 */
  DETAIL_003: { action: { type: 'toast' }, i18nKey: 'DETAIL_003' },
  /** 시안 복제 실패 */
  DETAIL_004: { action: { type: 'toast' }, i18nKey: 'DETAIL_004' },
  /** 시안 삭제 실패 */
  DETAIL_005: { action: { type: 'toast' }, i18nKey: 'DETAIL_005' },
  /** 시안 버전 불일치 (409 Conflict) → 최신 데이터 다시 조회 */
  DETAIL_006: { action: { type: 'refetch' } },
  /** 시안 편집 잠금 (다른 사용자가 편집 중) */
  DETAIL_007: { action: { type: 'stay' } },
  /** AI Chat 실패 */
  DETAIL_008: { action: { type: 'toast' }, i18nKey: 'DETAIL_008' },
  /** 마지막 상세페이지 삭제 시도 */
  DETAIL_009: { action: { type: 'toast' }, i18nKey: 'DETAIL_009' },
  /** 크레딧 부족 */
  CREDIT_001: { action: { type: 'stay' } },

  // ── Banner ──
  /** 배너 variation 이름 형식 오류 (rename 시 토스트는 호출부에서 처리) */
  BANNER_030: { action: { type: 'stay' } },
  /** 배너 variation 이름 중복 (rename 시 토스트는 호출부에서 처리) */
  BANNER_034: { action: { type: 'stay' } },

  // ── S3 ──
  /** 이미지 업로드 실패 */
  S3_001: { action: { type: 'toast' }, i18nKey: 'S3_001' },

  // ── User Sticker ──
  /** 지원하지 않는 스티커 이미지 포맷 */
  USER_STICKER_006: { action: { type: 'stay' } },

  // ── Video ──
  /** 영상 생성 실패 (일반) */
  VIDEO_001: { action: { type: 'toast' }, i18nKey: 'VIDEO_001' },
  /** 영상 조회 실패 */
  VIDEO_002: { action: { type: 'toast' }, i18nKey: 'VIDEO_002' },
  /** 영상 삭제 실패 */
  VIDEO_003: { action: { type: 'toast' }, i18nKey: 'VIDEO_003' },
  /** NOT FOUND */
  VIDEO_004: { action: { type: 'refetch' } },
  /** VIDEO_005, VIDEO_006: 내부에서 직접 컨트롤 */
};

export function getErrorEntry(
  code: string | undefined,
): ErrorCodeEntry | undefined {
  if (!code) {
    return undefined;
  }

  return ERROR_CODE_REGISTRY[code];
}

const REDIRECT_INTRO = publicRoutes.home();

/**
 * HTTP status code 기반으로 공통 에러 엔트리를 반환한다.
 * 서버 에러 코드(COM_001~006) 없이도 status만으로 처리 가능.
 *
 * - 401: 로케일 루트(/)로 redirect (로그인 만료)
 * - 403/5xx: boundary에서 ErrorFallback 표시 (action: boundary)
 * - 408: toast (타임아웃)
 */
export function getStatusEntry(status: number): ErrorCodeEntry | undefined {
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return { action: { type: 'boundary' }, i18nKey: 'COM_000' };
    case HTTP_STATUS.UNAUTHORIZED:
      return {
        action: { type: 'redirect', to: REDIRECT_INTRO },
        i18nKey: 'COM_001',
      };
    case HTTP_STATUS.FORBIDDEN:
      return { action: { type: 'boundary' }, i18nKey: 'COM_002' };
    case HTTP_STATUS.NOT_FOUND:
      return { action: { type: 'boundary' }, i18nKey: 'COM_404' };
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      return { action: { type: 'boundary' }, i18nKey: 'COM_003' };
    case HTTP_STATUS.NOT_IMPLEMENTED:
      return { action: { type: 'boundary' }, i18nKey: 'COM_004' };
    case HTTP_STATUS.BAD_GATEWAY:
      return { action: { type: 'boundary' }, i18nKey: 'COM_005' };
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
      return { action: { type: 'boundary' }, i18nKey: 'COM_008' };
    case HTTP_STATUS.WEB_SERVER_DOWN_ERROR:
      return { action: { type: 'boundary' }, i18nKey: 'COM_006' };
    case HTTP_STATUS.CONFLICT:
      return { action: { type: 'stay' } };
    case HTTP_STATUS.TIMEOUT:
      return { action: { type: 'toast' }, i18nKey: 'COM_007' };
    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      return { action: { type: 'toast' }, i18nKey: 'COM_999' };
    default:
      return undefined;
  }
}
